import { useCallback, useEffect, useState } from "react";
import AdminAuthGuard from "../components/admin/AdminAuthGuard";
import AdminLayout from "../components/admin/AdminLayout";
import AdminAuditsTable from "../components/admin/AdminAuditsTable";
import AdminPager from "../components/admin/AdminPager";
import { listAdminAudits } from "../lib/api/index";
import { logger } from "../lib/logger";

function Inner({ admin }) {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [paidFilter, setPaidFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const fetchPage = useCallback(async (p, s, pf) => {
    setLoading(true); setErr(null);
    try {
      const params = { page: p, page_size: pageSize };
      if (s) params.status_filter = s;
      if (pf !== "") params.paid = pf === "true";
      const data = await listAdminAudits(params);
      setRows(data.rows);
      setTotal(data.total);
      setTotalPages(data.total_pages);
    } catch (e) {
      logger.error("admin_audits_list_failed", { status: e?.response?.status });
      setErr("Could not load audits.");
    } finally { setLoading(false); }
  }, [pageSize]);

  useEffect(() => { fetchPage(page, statusFilter, paidFilter); }, [fetchPage, page, statusFilter, paidFilter]);

  return (
    <AdminLayout admin={admin}>
      <div className="sf-eyebrow" style={{ marginBottom: 8 }}>Audits</div>
      <h1 className="sf-display" style={{ fontSize: 28, marginBottom: 18 }} data-testid="admin-audits-headline">
        All audits.
      </h1>

      <div className="sf-card" style={{ marginBottom: 16, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }} data-testid="admin-audits-filters">
        <label className="sf-eyebrow" style={{ fontSize: 11 }}>Status
          <select value={statusFilter} onChange={(e) => { setPage(1); setStatusFilter(e.target.value); }}
                  className="sf-input" style={{ marginLeft: 8 }} data-testid="audits-filter-status">
            <option value="">all</option>
            <option value="in_progress">in_progress</option>
            <option value="completed">completed</option>
            <option value="abandoned">abandoned</option>
          </select>
        </label>
        <label className="sf-eyebrow" style={{ fontSize: 11 }}>Paid
          <select value={paidFilter} onChange={(e) => { setPage(1); setPaidFilter(e.target.value); }}
                  className="sf-input" style={{ marginLeft: 8 }} data-testid="audits-filter-paid">
            <option value="">all</option>
            <option value="true">paid</option>
            <option value="false">unpaid</option>
          </select>
        </label>
        <span className="sf-mono sf-muted-text" style={{ marginLeft: "auto", fontSize: 12 }} data-testid="audits-total">
          {total} total
        </span>
      </div>

      {err && <div className="sf-card" data-testid="admin-audits-error">{err}</div>}

      <AdminAuditsTable rows={rows} loading={loading} />

      <AdminPager
        page={page}
        totalPages={totalPages}
        onPrev={() => setPage((p) => Math.max(1, p - 1))}
        onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
      />
    </AdminLayout>
  );
}

export default function AdminAudits() {
  return <AdminAuthGuard>{(admin) => <Inner admin={admin} />}</AdminAuthGuard>;
}
